import React, { useState } from 'react';
import { Mail, MapPin, Phone } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { 
  sanitizeInput, 
  validateEmail, 
  checkRateLimit,
  detectSuspiciousActivity 
} from '../utils/security';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // SECURITY: Detect suspicious patterns
    if (detectSuspiciousActivity(value)) {
      toast({
        title: "Invalid input detected",
        description: "Please remove special characters and try again.",
      });
      return;
    }
    
    setFormData({
      ...formData,
      [name]: sanitizeInput(value)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // SECURITY: Rate limiting
    const rateCheck = checkRateLimit('contact', 5, 300000); // 5 attempts per 5 minutes
    if (!rateCheck.allowed) {
      toast({
        title: "Too many submissions",
        description: `Please wait before sending another message. Try again at ${rateCheck.resetTime.toLocaleTimeString()}`,
      });
      return;
    }
    
    // SECURITY: Validate email
    if (!validateEmail(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
      });
      return;
    }
    
    toast({
      title: "Message sent!",
      description: "We'll get back to you within 24-48 hours.",
    });
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <h1 className="text-3xl font-medium tracking-wide mb-8">CONTACT US</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Contact Form */}
        <div>
          <h2 className="text-xl font-medium mb-6">Send us a message</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              name="name"
              placeholder="Your name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
            />
            <input
              type="email"
              name="email"
              placeholder="Your email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
            />
            <input
              type="text"
              name="subject"
              placeholder="Subject"
              required
              value={formData.subject}
              onChange={handleChange}
              className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
            />
            <textarea
              name="message"
              placeholder="Your message"
              required
              value={formData.message}
              onChange={handleChange}
              rows="6"
              className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors resize-none"
            />
            <button
              type="submit"
              className="w-full bg-black text-white py-3 text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
            >
              SEND MESSAGE
            </button>
          </form>
        </div>

        {/* Contact Information */}
        <div>
          <h2 className="text-xl font-medium mb-6">Get in touch</h2>
          <div className="space-y-6">
            <div className="flex gap-4">
              <Mail className="flex-shrink-0 mt-1" size={20} />
              <div>
                <h3 className="font-medium mb-1">Email</h3>
                <p className="text-gray-600 text-sm">support@ditris.com</p>
                <p className="text-gray-600 text-sm">orders@ditris.com</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <Phone className="flex-shrink-0 mt-1" size={20} />
              <div>
                <h3 className="font-medium mb-1">Phone</h3>
                <p className="text-gray-600 text-sm">+1 (555) 123-4567</p>
                <p className="text-gray-500 text-xs mt-1">Mon-Fri, 9am-6pm EST</p>
              </div>
            </div>

            <div className="flex gap-4">
              <MapPin className="flex-shrink-0 mt-1" size={20} />
              <div>
                <h3 className="font-medium mb-1">Address</h3>
                <p className="text-gray-600 text-sm">
                  Dïtris Headquarters<br />
                  123 Fashion Street<br />
                  New York, NY 10001<br />
                  United States
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="font-medium mb-4">Business Hours</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Monday - Friday</span>
                <span>9:00 AM - 6:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span>Saturday</span>
                <span>10:00 AM - 4:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span>Sunday</span>
                <span>Closed</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;